<template>
  <h2 :class="$style.title">カテゴリー「{{ categoryName }}」の{{ isVintage ? 'アンティーク・ヴィンテージ商品' : '作品' }}</h2>
  <template v-if="isLoaded">
    <template v-if="items.length > 0">
      <ul :class="$style.list">
        <li v-for="item in items" :key="item.itemId">
          <a :href="`/listing/item/${item.itemId}`">
            <img :src="itemImageUrl(item.imagePath)" :alt="item.title" width="155" height="155" loading="lazy" />
          </a>
          <div :class="$style.name">
            <a :href="`/listing/item/${item.itemId}`">{{ item.title }}</a>
          </div>
          <div>&yen;{{ item.price.toLocaleString() }}</div>
        </li>
      </ul>
      <p :class="$style.more"><a :href="linkToCategory">もっと見る</a></p>
    </template>
    <p v-else :class="$style.noItems">同じカテゴリーの作品はありません</p>
  </template>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { dynamicImageUrl } from '@/js/lib/helper/url';
import { getSameCategoryItems, type SameCategoryItem } from '@/js/lib/api/item';
import { category as categoryData } from '@/js/data/category';

const props = defineProps<{
  categoryId: number;
  isVintage: boolean;
  price: number;
}>();
const isLoaded = ref(false);
const items = ref<SameCategoryItem[]>([]);
const category = categoryData({ categoryId: props.categoryId });
const categoryName = category?.name || '';
const linkToCategory = computed(() => {
  let path = '/listing';
  if (category) {
    if (category.parentCategoryId) {
      const parent = categoryData({ categoryId: category.parentCategoryId })!;
      path = `/category/${parent.slug}/${category.slug}`;
    } else {
      path = `/category/${category.slug}`;
    }
  }

  if (props.isVintage) {
    path += '?vintage=on';
  }

  return path;
});
const itemImageUrl = (imagePath: string) => dynamicImageUrl(imagePath, 310, 310);
const loadItems = async () => {
  const data = await getSameCategoryItems({ categoryId: props.categoryId, price: props.price, isVintage: props.isVintage });
  items.value = data.slice(0, 18);
  isLoaded.value = true;
};

onMounted(() => {
  loadItems();
});
</script>

<style module lang="scss">
.title {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 12px;

  @media (--sp) {
    margin-bottom: 10px;
  }
}

.list {
  display: flex;
  gap: 10px;
  overflow-x: auto;

  @media (--sp) {
    margin: 0 -10px;
    padding: 0 0 0 10px;
  }

  > li {
    min-width: calc(15.4% - 10px);
    padding-bottom: 12px;
    position: relative;
    width: calc(15.4% - 10px);

    @media (--sp) {
      min-width: calc(28.6% - 10px);
      width: calc(28.6% - 10px);
    }

    &:last-child::after {
      content: '';
      height: 1px;
      left: 100%;
      position: absolute;
      top: 0;
      width: 10px;
    }

    img {
      height: auto;
      width: 100%;
    }

    .name {
      margin-top: 6px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.noItems {
  align-items: center;
  background-color: var(--color-gray100);
  display: flex;
  justify-content: center;
  min-height: 199.91px;

  @media (--sp) {
    min-height: calc(28.6vw + 46.14px); // 画像の高さが可変なので計算済みの値を設定
  }
}

.more {
  margin-top: 10px;
  text-align: right;

  a {
    padding-left: 12px;
    position: relative;

    &::before {
      content: $icon-arrow-right;
      font-family: $font-icon;
      font-size: 18px;
      left: -5px;
      line-height: 1;
      position: absolute;
      top: -2px;
    }
  }
}
</style>
